import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { TOKEN_KEY, TENANT_KEY } from "./api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);
export const usePermissions = () => {
  const ctx = useContext(AuthCtx);
  return { perms: ctx?.perms, scope: ctx?.permScope, permsLoaded: ctx?.permsLoaded, can: ctx?.can, refreshPerms: ctx?.refreshPerms };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenantId, setTenantIdState] = useState(localStorage.getItem(TENANT_KEY) || "");
  const [loading, setLoading] = useState(true);
  const [perms, setPerms] = useState(null);
  const [permScope, setPermScope] = useState(null);
  const [permsLoaded, setPermsLoaded] = useState(false);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/permissions");
      setPerms(data.permissions || {});
      setPermScope(data.scope || null);
    } catch {
      setPerms({});
    } finally {
      setPermsLoaded(true);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadPerms();
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, [loadPerms]);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, data.access_token);
    if (data.role !== "super_admin" && data.tenant_id) {
      localStorage.setItem(TENANT_KEY, data.tenant_id);
      setTenantIdState(data.tenant_id);
    } else {
      localStorage.removeItem(TENANT_KEY);
      setTenantIdState("");
    }
    setUser(data);
    await loadPerms();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.clear();
    setUser(null);
    setTenantIdState("");
    setPerms(null);
    setPermScope(null);
    setPermsLoaded(false);
  };

  const setTenantId = (id) => {
    if (id) localStorage.setItem(TENANT_KEY, id);
    else localStorage.removeItem(TENANT_KEY);
    setTenantIdState(id);
  };

  const isSuperAdmin = user?.role === "super_admin";
  const activeTenant = isSuperAdmin ? tenantId : user?.tenant_id;

  const can = useCallback((module, action = "view") => {
    if (!permsLoaded) return true;
    const m = (perms || {})[module];
    if (!m) return true;
    return !!m[action];
  }, [perms, permsLoaded]);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh: loadMe, isSuperAdmin, tenantId, setTenantId, activeTenant, perms, permScope, permsLoaded, can, refreshPerms: loadPerms }}>
      {children}
    </AuthCtx.Provider>
  );
};
